import moment from "moment";
import Grid from "@mui/material/Grid";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { Autocomplete, Box, DatePicker, FormField, Modal } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import ReportService from "services/medkit/report.service";

import { ICodename } from "commons/interfaces";
import { setDataAlert, showLoading } from "store/slice/common.slice";
import { IncidentReportTemplate } from "constants/enum";


const TEMPLATE_OPTIONS: ICodename[] = [
    { code: IncidentReportTemplate.Default, name: Strings.CHART.TEMPLATE_DEFAULT },
    { code: IncidentReportTemplate.SoYTe, name: Strings.CHART.TEMPLATE_SO_Y_TE },
];

const MONTH_OPTIONS_ALL = -1;
const MONTH_OPTIONS = [
    { code: MONTH_OPTIONS_ALL, name: Strings.CHART.ALL },
    { code: 1, name: `${Strings.CHART.MONTH} 1` },
    { code: 2, name: `${Strings.CHART.MONTH} 2` },
    { code: 3, name: `${Strings.CHART.MONTH} 3` },
    { code: 4, name: `${Strings.CHART.MONTH} 4` },
    { code: 5, name: `${Strings.CHART.MONTH} 5` },
    { code: 6, name: `${Strings.CHART.MONTH} 6` },
    { code: 7, name: `${Strings.CHART.MONTH} 7` },
    { code: 8, name: `${Strings.CHART.MONTH} 8` },
    { code: 9, name: `${Strings.CHART.MONTH} 9` },
    { code: 10, name: `${Strings.CHART.MONTH} 10` },
    { code: 11, name: `${Strings.CHART.MONTH} 11` },
    { code: 12, name: `${Strings.CHART.MONTH} 12` },
] as ICodename<number>[];

const reportService = new ReportService();

const ModalSummaryUnexpectedIncident = ({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
    const dispatch = useDispatch();
    const [error, setError] = useState<{
        dateTime?: string,
        template?: string,
    }>({});
    const [data, setData] = useState<{
        endDate?: any,
        startDate?: any,
        template?: number,
        year?: number,
        months?: number[],
    }>({
        startDate: moment().startOf("month").unix(),
        endDate: moment().endOf("month").unix(),
        template: IncidentReportTemplate.Default,
        year: moment().year(),
        months: [MONTH_OPTIONS_ALL],
    });

    const handleValidateData = () => {
        let checked = true;
        let newErr: any = { ...error };
        if (
            !Helpers.isNullOrEmpty(data?.endDate) &&
            !Helpers.isNullOrEmpty(data?.startDate) &&
            (data?.endDate !== "0") && (data?.endDate !== 0) &&
            (data?.startDate !== "0") && (data?.startDate !== 0)
        ) {
            newErr["dateTime"] = undefined;
        } else {
            checked = false;
            newErr["dateTime"] = Strings.CHART.PLEASE_ENTER_DATE_TIME;
        }

        if (Helpers.isNullOrEmpty(data?.template)) {
            checked = false;
            newErr["template"] = Strings.CHART.PLEASE_ENTER_TEMPLATE;
        } else {
            newErr["template"] = undefined;
        }

        !checked && setError(newErr);

        return checked;
    };

    const handleSubmit = async () => {
        if (!handleValidateData()) {
            return;
        } else {
            try {
                setError({});
                dispatch(showLoading(true));

                let templateParams: { year: number; months?: number[]; } = { year: data?.year || moment().year() };
                if (data?.template === IncidentReportTemplate.SoYTe) {
                    const hasAll = data?.months?.includes(MONTH_OPTIONS_ALL);
                    templateParams.months = hasAll ? undefined : data?.months;
                }

                const result = await reportService.generatingUnexpectedIncidentReport({
                    endTime: data?.endDate,
                    startTime: data?.startDate,
                    template: data?.template,
                    ...templateParams,
                });

                const reponseBlob = new Blob([result]);

                const fileName = data?.template === IncidentReportTemplate.SoYTe
                    ? `Bao cao su co - So Y Te.doc`
                    : `Bao cao su co.doc`

                const downloadUrl = URL.createObjectURL(reponseBlob);

                const a = document.createElement("a");

                a.href = downloadUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();

                URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(a);
                dispatch(setDataAlert({ message: Strings.REPORT.EXPORT_FILE_REPORT_SUCCESS, type: "success" }));
            } catch (error) {
                let message = Helpers.handleExceptionError(error);
                dispatch(setDataAlert({ message, type: "error" }));
            } finally {
                dispatch(showLoading(false));
            }
        }
    };

    return (
        <Modal
            fullWidth
            allowOutsideClick
            visible={open}
            maxWidth={"md"}
            hasActionButton
            onClose={() => {
                setOpen(false);
            }}
            onAction={() => {
                handleSubmit();
            }}
            buttonAction={Strings.COMMON.OK}
            closeButton={Strings.COMMON.CANCEL}
            title={Strings.CHART.CONDITIONS_FOR_REPORTING}
        >
            <Box sx={{ padding: "0px 16px" }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} className="mt-3">
                        <Autocomplete
                            variant="outlined"
                            data={TEMPLATE_OPTIONS}
                            label={Strings.CHART.TEMPLATE}
                            placeholder={Strings.CHART.TEMPLATE}
                            defaultValue={String(data?.template)}
                            errorMessage={error?.template}
                            onChange={(value) => {
                                setData(prev => ({
                                    ...prev,
                                    template: Helpers.isNullOrEmpty(value) ? undefined : Number(value),
                                }));
                            }}
                        />
                    </Grid>

                    {data?.template === IncidentReportTemplate.SoYTe ? (
                        <>
                            <Grid item xs={6}>
                                <FormField
                                    variant={"outlined"}
                                    label={Strings.CHART.YEAR}
                                    type="number"
                                    maxLength={4}
                                    defaultValue={data?.year}
                                    placeholder={Strings.CHART.ENTER_YEAR_FOR_REPORT}
                                    onBlur={(year) => {
                                        setData(prev => ({
                                            ...prev,
                                            year: Number(year),
                                        }));
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    variant="outlined"
                                    data={MONTH_OPTIONS}
                                    label={Strings.CHART.MONTH}
                                    placeholder={Strings.CHART.SELECT_MONTH}
                                    defaultValue={data?.months}
                                    multiple
                                    onChange={(values: number[]) => {
                                        let filterValues = JSON.parse(JSON.stringify(values ?? []));

                                        if (values.some(month => month !== MONTH_OPTIONS_ALL)) {
                                            filterValues = values.filter(month => month !== MONTH_OPTIONS_ALL);
                                        }

                                        setData(prev => ({ ...prev, months: filterValues }));
                                    }}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="Từ"
                                    placeholder="Nhập thời gian"
                                    value={data?.startDate * 1000}
                                    onChangeValue={(value) => {
                                        const val = !Helpers.isNullOrEmpty(value) ? moment(value).startOf("day").unix() : undefined;
                                        setData(prev => ({
                                            ...prev,
                                            startDate: val,
                                        }));
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="Đến"
                                    placeholder="Nhập thời gian"
                                    value={data?.endDate * 1000}
                                    onChangeValue={(value) => {
                                        const val = !Helpers.isNullOrEmpty(value) ? moment(value).endOf("day").unix() : undefined;
                                        setData(prev => ({
                                            ...prev,
                                            endDate: val,
                                        }));
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        </Modal>
    );
};


export default ModalSummaryUnexpectedIncident;